import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Search, LocateFixed } from "lucide-react";
import { extractLocationDetails } from "./onboardingUtils";

const DEFAULT_CENTER = [20.5937, 78.9629]; // India

// The interactive map itself: fixed center pin (Uber/Swiggy style — the marker never
// moves, the map moves under it), floating search bar, and a "use my location" button.
// Position is reported to the parent via onChange on every drag/search/geolocate.
export default function LocationMapPicker({
  latitude,
  longitude,
  onChange,
  onResolvedAddress,
  onLocationDetails,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const searchDebounceRef = useRef(null);
  // Search results already carry an authoritative display_name; a Nominatim reverse-geocode
  // of the same point can resolve to a different nearby feature, so skip the moveend-triggered
  // reverse geocode after we've just panned there from a search selection.
  const suppressNextReverseGeocodeRef = useRef(false);

  const hasPosition = latitude !== "" && latitude !== null && longitude !== "" && longitude !== null;

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`
      );
      const data = await res.json();
      onResolvedAddress?.(data?.display_name || "");
      onLocationDetails?.(extractLocationDetails(data?.address));
    } catch (error) {
      console.error("Reverse geocode failed:", error);
    }
  };

  const moveTo = (lat, lng, zoom) => {
    if (mapRef.current) mapRef.current.setView([lat, lng], zoom ?? mapRef.current.getZoom());
  };

  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    const initialCenter = hasPosition ? [Number(latitude), Number(longitude)] : DEFAULT_CENTER;
    const map = L.map(mapContainerRef.current, { zoomControl: false }).setView(
      initialCenter,
      hasPosition ? 16 : 5
    );
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    L.control.zoom({ position: "bottomright" }).addTo(map);

    map.on("moveend", () => {
      const center = map.getCenter();
      onChange(center.lat, center.lng);
      if (suppressNextReverseGeocodeRef.current) {
        suppressNextReverseGeocodeRef.current = false;
        return;
      }
      reverseGeocode(center.lat, center.lng);
    });

    mapRef.current = map;
    // Fix Leaflet's initial render sizing inside a modal (container isn't laid out yet on mount).
    setTimeout(() => map.invalidateSize(), 0);
    if (hasPosition) reverseGeocode(Number(latitude), Number(longitude));

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    if (!searchQuery.trim()) {
      setSuggestions([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(searchQuery.trim())}`
        );
        setSuggestions(await res.json());
      } catch (error) {
        console.error("Location search failed:", error);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery]);

  const selectSuggestion = (result) => {
    const lat = Number(result.lat);
    const lng = Number(result.lon);
    onResolvedAddress?.(result.display_name || "");
    onLocationDetails?.(extractLocationDetails(result.address));
    setSearchQuery(result.display_name || "");
    setSuggestions([]);
    onChange(lat, lng);
    suppressNextReverseGeocodeRef.current = true;
    moveTo(lat, lng, 16);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        onChange(lat, lng);
        moveTo(lat, lng, 16);
        reverseGeocode(lat, lng);
        setLocating(false);
      },
      (error) => {
        console.error("Geolocation failed:", error);
        setLocating(false);
      }
    );
  };

  return (
    <div className="relative w-full h-full rounded-lg overflow-hidden border border-gray-200">
      <div ref={mapContainerRef} className="absolute inset-0" />

      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full z-[500]">
        <MapPin size={34} className="text-[#e63946] drop-shadow-md" fill="#e63946" strokeWidth={1.5} />
      </div>

      <div className="absolute top-3 left-3 right-3 z-[500]">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search a location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 shadow-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84]"
          />
        </div>
        {(suggestions.length > 0 || searching) && (
          <div className="mt-1 max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-md">
            {searching && <p className="px-3 py-2 text-xs text-gray-500">Searching...</p>}
            {suggestions.map((result) => (
              <button
                key={`${result.place_id}`}
                type="button"
                onClick={() => selectSuggestion(result)}
                className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                {result.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={useCurrentLocation}
        disabled={locating}
        title="Use my current location"
        className="absolute bottom-3 right-3 z-[500] h-10 w-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center text-[#004276] hover:bg-gray-50 disabled:opacity-60"
      >
        <LocateFixed size={18} className={locating ? "animate-pulse" : ""} />
      </button>
    </div>
  );
}
